import { cn } from '@/lib/utils'

import { GiftBadge } from './GiftBadge'
import { LockerDeliveryBadge } from './LockerDeliveryBadge'
import { NewBadge } from './NewBadge'
import { ProductButton } from './ProductButton'
import { ProductImage } from './ProductImage'
import { ProductName } from './ProductName'
import { ProductPrice } from './ProductPrice'
import { RewardPoints } from './RewardPoints'

import type { ProductCardProps } from '../../types'

const CONTAINER_CLASSES = 'group relative bg-white p-0 justify-between items-center gap-1 border-2 border-white hover:border-text-color transition-colors duration-300 grid grid-rows-2 w-full overflow-hidden h-[400px] xl:h-[430px]'

const CONTENT_CLASSES = 'row-span-1 px-2 grid grid-rows-4 gap-1 h-[195px] w-full min-w-0'

/**
 * ProductCard - Reusable product card UiBlock matching CardItem.tsx design
 *
 * Grid layout: 2 rows (image + content).
 * Features: gift/locker badges, reward points, discount percentage, stock status.
 *
 * @example
 * import { ProductCard, type Product } from '../../UiBlocks'
 *
 * <ProductCard product={product} priceSuffix="cu TVA" />
 */
export function ProductCard({
  product,
  className,
  priceSuffix = 'cu TVA',
  imageLoading = 'lazy',
  showButton = true,
  renderActions,
}: ProductCardProps) {
  return (
    <div className={cn(CONTAINER_CLASSES, className)}>
      {/* New Badge (absolute top-left) */}
      {product.isNew && <NewBadge />}

      {/* Gift Badge (absolute left) */}
      {product.showGiftBadge && <GiftBadge />}

      {/* Locker Delivery Badge (absolute right) */}
      {product.showLockerDelivery && <LockerDeliveryBadge />}

      {/* Product Image */}
      <ProductImage
        name={product.name}
        url={product.url}
        imageUrl={product.imageUrl}
        imageLoading={imageLoading}
      />

      {/* Content Grid: name, reward points, price, button */}
      <div className={CONTENT_CLASSES}>
        <ProductName
          name={product.name}
          url={product.url}
          h2ProductName={product.h2ProductName}
        />

        <RewardPoints points={product.rewardPoints} />

        <ProductPrice
          price={product.price}
          specialPrice={product.specialPrice}
          msrp={product.msrp}
          priceSuffix={priceSuffix}
        />

        {showButton && (
          <ProductButton
            url={product.url}
            sku={product.sku}
            productType={product.productType}
            isInStock={product.isInStock}
            showAddToCart={product.showAddToCart}
            isNotForSale={product.isNotForSale}
            isDiscontinued={product.isDiscontinued}
            actionSlot={renderActions?.(product)}
          />
        )}
      </div>
    </div>
  )
}
